// src/components/admin/editors/AboutEditor.js
import React, { useState, useEffect } from "react";
import { db } from "../../../firebaseConfig/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Save, Upload, X } from "lucide-react";
import { uploadImage, deleteImageByUrl } from "../../../utils/imageUtils";

const AboutEditor = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [aboutData, setAboutData] = useState({
    title: "",
    subtitle: "",
    profileImage: "",
  });

  useEffect(() => {
    loadAboutData();
  }, []);

  const loadAboutData = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, "content", "about");
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setAboutData(docSnap.data());
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      setMessage({ type: "error", text: "Error al cargar los datos" });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setMessage({
        type: "info",
        text: "Convirtiendo a WebP y subiendo imagen...",
      });

      // Si ya existe una imagen, eliminarla primero
      if (aboutData.profileImage) {
        await deleteImageByUrl(aboutData.profileImage);
      }

      // Subir nueva imagen (automáticamente convertida a WebP)
      const url = await uploadImage(file, "about/profile");

      setAboutData({ ...aboutData, profileImage: url });
      setMessage({
        type: "success",
        text: "Imagen optimizada y subida correctamente (WebP 90%)",
      });
    } catch (error) {
      console.error("Error al subir imagen:", error);
      setMessage({ type: "error", text: "Error al subir la imagen" });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const docRef = doc(db, "content", "about");
      await setDoc(docRef, aboutData);
      setMessage({ type: "success", text: "Cambios guardados correctamente" });
    } catch (error) {
      console.error("Error al guardar:", error);
      setMessage({ type: "error", text: "Error al guardar los cambios" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Message */}
      {message.text && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : message.type === "error"
                ? "bg-red-50 text-red-800 border border-red-200"
                : "bg-blue-50 text-blue-800 border border-blue-200"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {/* Imagen de perfil */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Imagen de Perfil
          </h3>

          <div className="flex flex-col items-center space-y-4">
            {aboutData.profileImage ? (
              <div className="relative">
                <img
                  src={aboutData.profileImage}
                  alt="Profile"
                  className="w-48 h-48 rounded-full object-cover shadow-lg"
                />
                <button
                  onClick={() =>
                    setAboutData({ ...aboutData, profileImage: "" })
                  }
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 shadow-lg"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-48 h-48 border-2 border-dashed border-gray-300 rounded-full cursor-pointer hover:border-blue-400 transition-all">
                <Upload className="text-gray-400 mb-2" size={32} />
                <span className="text-sm text-gray-600">Subir imagen</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {/* Contenido de texto */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Contenido de la Sección
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título
              </label>
              <input
                type="text"
                value={aboutData.title}
                onChange={(e) =>
                  setAboutData({ ...aboutData, title: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Mi Mirada"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción / Subtítulo
              </label>
              <textarea
                value={aboutData.subtitle}
                onChange={(e) =>
                  setAboutData({ ...aboutData, subtitle: e.target.value })
                }
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Escribe sobre tu trabajo, filosofía y enfoque..."
              />
              <p className="mt-1 text-sm text-gray-500">
                {aboutData.subtitle.length} caracteres
              </p>
            </div>
          </div>
        </div>

        {/* Vista previa */}
        <div className="bg-black rounded-xl shadow-sm p-8 border border-gray-200">
          <h3 className="text-lg font-semibold text-white mb-6">
            Vista Previa
          </h3>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Imagen */}
            <div className="flex justify-center">
              {aboutData.profileImage ? (
                <img
                  src={aboutData.profileImage}
                  alt="Preview"
                  className="w-64 h-64 rounded-full object-cover grayscale"
                />
              ) : (
                <div className="w-64 h-64 rounded-full bg-gray-800 flex items-center justify-center">
                  <span className="text-gray-500">Sin imagen</span>
                </div>
              )}
            </div>

            {/* Texto */}
            <div>
              <h2 className="text-3xl font-light text-white mb-6 tracking-wide">
                {aboutData.title || "Título"}
              </h2>
              <p className="text-white/80 text-sm leading-relaxed font-light">
                {aboutData.subtitle || "Descripción..."}
              </p>
            </div>
          </div>
        </div>

        {/* Botón guardar */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            <span>{saving ? "Guardando..." : "Guardar cambios"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutEditor;
